/**
 * Whether a frame of pixels has a picture in it — three numbers, and the reason
 * they exist.
 *
 * 3D COCKPIT phase 1 shipped a dashboard that drew nothing, for weeks, while its
 * probe reported the quad visible, mapped, positioned to six decimal places and
 * freshly uploaded thirty-six times (`docs/engineering/cockpit-3d-design.md`
 * §11). Every one of those readings was about the quad's *intent*, and intent is
 * exactly what survives a bug. A cleared canvas and the Camel's instrument panel
 * produced identical telemetry, because nothing anywhere asked what was in the
 * picture.
 *
 * This is the question nobody was asking, and it lives in the baker rather than
 * beside the runtime for the same reason `cockpit-airframe.ts` does: it is pure
 * arithmetic that both the app and a bake-time test against the real game data
 * need, and the app re-exports it (`apps/web/src/flight/cockpit-dash-quad.ts`).
 */

/**
 * What a frame of RGBA pixels actually contains.
 *
 * Two numbers rather than one, because a frame can be empty in two ways and each
 * hides from the other. A **cleared** canvas gives itself away on `inkFraction`
 * and looks perfectly ordinary on `variance` (0 is also what a real picture would
 * never be, but you have to look). A canvas **filled flat** with one colour scores
 * `inkFraction` 1.0 — a clean bill of health — and gives itself away only on
 * `variance`. `mean` places the picture on the light-dark axis, so a frame that
 * has gone all-black or all-white is not mistaken for art.
 */
export interface FrameStats {
  /** Fraction of sampled pixels with any alpha at all — 0 on a cleared canvas. */
  readonly inkFraction: number;
  /** Mean luminance over the sampled pixels, 0-255. */
  readonly mean: number;
  /** Variance of that luminance — 0 for any frame of one flat colour. */
  readonly variance: number;
}

/**
 * The three numbers, from one RGBA buffer (`getImageData(...).data`, or one built
 * by hand from decoded sprite sheets).
 *
 * `stride` samples every n-th pixel. The question is "is there anything here",
 * not a histogram, so on a 320x200 cockpit frame a stride of 4 costs a sixteenth
 * of the work and answers it just as well.
 */
export function frameStats(pixels: ArrayLike<number>, stride = 1): FrameStats {
  const step = Math.max(1, Math.trunc(stride)) * 4;
  let inked = 0;
  let sum = 0;
  let sumSquares = 0;
  let samples = 0;
  for (let index = 0; index + 3 < pixels.length; index += step) {
    if ((pixels[index + 3] ?? 0) > 0) inked++;
    const luminance = ((pixels[index] ?? 0) + (pixels[index + 1] ?? 0) + (pixels[index + 2] ?? 0)) / 3;
    sum += luminance;
    sumSquares += luminance * luminance;
    samples++;
  }
  if (samples === 0) return { inkFraction: 0, mean: 0, variance: 0 };
  const mean = sum / samples;
  return { inkFraction: inked / samples, mean, variance: Math.max(0, sumSquares / samples - mean * mean) };
}

/** Whether a frame carries a picture rather than a cleared or flat-filled canvas — both failure modes, in one boolean. */
export function frameIsPainted(stats: FrameStats): boolean {
  return stats.inkFraction > 0 && stats.variance > 0;
}
